/**
 * Lógica pura para descartar una sesión sin series.
 *
 * Como `coalesce.ts`, no toca Dexie ni la red: son funciones sobre datos para
 * poder comprobarlas en Node. Quien las usa es `session.ts` (descartar) y
 * `syncQueue.ts` (la operación `workout.delete`).
 */
import type { WorkoutSyncRequest } from '@fitness/api-client';
import type { SyncOp } from '@fitness/core';

import { WORKOUT_SYNC_KIND } from './coalesce.ts';

/**
 * Tipo de operación de la cola que borra una sesión en el servidor
 * (`DELETE /v1/workouts/{clientId}`, idempotente). Es aparte de `workout.sync`
 * a propósito: la fusión de lotes no la toca y viaja en su propio turno.
 */
export const WORKOUT_DELETE_KIND = 'workout.delete';

/** Carga de una operación `workout.delete`. */
export interface WorkoutDeletePayload {
  clientId: string;
}

/** Lee la carga de una operación `workout.delete`; `null` si está corrupta. */
export function deletePayload(payload: unknown): WorkoutDeletePayload | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const clientId = (payload as { clientId?: unknown }).clientId;
  return typeof clientId === 'string' && clientId !== '' ? { clientId } : null;
}

/** Cambios que hay que aplicar a la cola para olvidar una sesión. */
export interface StripResult {
  /** Operaciones que se quedan vacías y sobran. */
  removeIds: string[];
  /** Operaciones que siguen teniendo algo que enviar, ya sin la sesión. */
  updated: SyncOp[];
}

function isEmptyRequest(request: WorkoutSyncRequest): boolean {
  return (
    (request.sessions ?? []).length === 0 &&
    (request.sets ?? []).length === 0 &&
    (request.deletedSetClientIds ?? []).length === 0
  );
}

/**
 * Saca de las operaciones `workout.sync` pendientes la sesión y sus series.
 *
 * Una sesión descartada no debe volver a subir: si su alta siguiera en la cola
 * (sin red, o esperando un reintento), el servidor la crearía después de
 * haberla borrado. El resto del lote se conserva tal cual, con sus intentos y
 * su `nextAttemptAt`: esto no cambia cuándo se reintenta nada, sólo qué lleva.
 */
export function stripSessionFromOps(ops: readonly SyncOp[], sessionClientId: string): StripResult {
  const removeIds: string[] = [];
  const updated: SyncOp[] = [];

  for (const op of ops) {
    if (op.kind !== WORKOUT_SYNC_KIND) continue;
    if (typeof op.payload !== 'object' || op.payload === null) continue;
    const request = op.payload as WorkoutSyncRequest;
    const sessions = request.sessions ?? [];
    const sets = request.sets ?? [];
    const keptSessions = sessions.filter((session) => session.clientId !== sessionClientId);
    const keptSets = sets.filter((set) => set.sessionClientId !== sessionClientId);
    if (keptSessions.length === sessions.length && keptSets.length === sets.length) continue;

    const next: WorkoutSyncRequest = { ...request };
    if (request.sessions !== undefined) next.sessions = keptSessions;
    if (request.sets !== undefined) next.sets = keptSets;
    if (isEmptyRequest(next)) removeIds.push(op.id);
    else updated.push({ ...op, payload: next });
  }

  return { removeIds, updated };
}

/**
 * Sesión abierta y sin ninguna serie: la que se ofrece descartar al salir o
 * al terminar, y la que el historial marca como «Sin terminar».
 */
export function isEmptyOpenSession(
  session: { finishedAt: string | null } | null | undefined,
  setsCount: number,
): boolean {
  return session != null && session.finishedAt === null && setsCount === 0;
}
